/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, VStack } from 'chakra-v3';
import { Text } from '../../components/Text';

interface NoteCardProps {
  number: string;
  domain: string;
  content: string;
  bgColor: 'bg.mintWhite' | 'bg.limeWhite';
}

const NoteCard: React.FC<NoteCardProps> = ({ number, domain, content, bgColor }) => (
  <Box
    bg={bgColor}
    border="1px solid"
    borderColor="rgba(10, 49, 60, 0.1)"
    p={{ base: '16px', sm: '18px', md: '20px' }}
    w="100%"
  >
    <VStack gap="27px" alignItems="flex-start" w="100%">
      {/* Header: Label + Gradient Separator */}
      <VStack gap="8px" alignItems="flex-start" w="100%">
        <Text
          fontFamily="heading"
          fontSize="14px"
          fontWeight="normal"
          lineHeight="normal"
          letterSpacing="-1px"
          color="text.teal"
        >
          {number} | {domain}
        </Text>

        {/* Gradient Separator Line */}
        <Box
          w="100%"
          h="1.5px"
          bg="radial-gradient(293.86% 142.07% at 1.02% 0%, var(--chakra-colors-brand-teal) 0%, var(--chakra-colors-brand-teal-light) 100%)"
        />
      </VStack>

      {/* Body Text */}
      <Text variant="noteBody">{content}</Text>
    </VStack>
  </Box>
);

const notesData: Omit<NoteCardProps, 'bgColor'>[] = [
  {
    number: '01',
    domain: 'Healthcare',
    content:
      'The healthcare workshop showed that a useful chatbot must go beyond medical accuracy to offer responses that are practical, specific and emotionally sensitive. Experts stressed firm safety boundaries, emphasizing key "don\'ts": no prescriptive advice, no dosage guidance, and no long explanatory text that risks confusion. A key insight was how Fear of Finding Out (FoFo) shapes behaviour during urgent health concerns\u2014when users are already anxious, offering home remedies alongside "seek medical care" can unintentionally delay treatment. Contributors also emphasised the value of clear bullet points, empathetic language, and multi-turn questions to understand whether someone is a patient, caregiver or frontline worker. Together, these inputs outline what trustworthy and safe health information delivery requires.',
  },
  {
    number: '02',
    domain: 'Education',
    content:
      "The education workshop showed that evaluating chatbots requires close attention to user intent, language nuance and a learner\u2019s context. Students and job seekers often use imprecise language, combine multiple questions, or seek direction without fully knowing how to express their goals. In these moments, a useful system should probe, clarify and help users reflect rather than offer quick, generic answers. Regional context also shapes aspirations and pathways, yet current models often default to mainstream digital or AI-related vocational tracks. A partner from Nagaland noted how this misses local realities: many young people there aspire to move to larger cities to pursue beauty and spa training. Such insights underscore why location and language context are essential to meaningful guidance.",
  },
  {
    number: '03',
    domain: 'Finance',
    content:
      "The finance workshop showed that evaluating chatbots in financial contexts requires close attention to socio-economic realities, user intent and linguistic accessibility. Many model responses assume familiarity with formal banking systems and technical terminology, overlooking the constraints faced by low-income users, particularly women in rural and semi-urban communities. Experts emphasised that useful financial guidance must begin by clarifying why funds are needed, in what amounts, and under what constraints, rather than offering abstract or generic advice. Step-by-step explanations, simple numerical examples, and conversational follow-up questions were seen as essential to building understanding and trust. Participants also stressed the importance of aligning responses with regional practices and clearly stating limitations. These insights highlight the need for culturally grounded, context-aware financial guidance.",
  },
  {
    number: '04',
    domain: 'Legal',
    content:
      "The legal workshop surfaced that evaluating chatbots in legal contexts requires an exceptionally high bar for accuracy, precision and tone. Experts stressed that even a single incorrect statutory reference or fabricated case citation can carry serious real-world consequences and erode trust. Beyond technical correctness, responses must acknowledge emotional vulnerability, particularly in sensitive matters such as workplace harassment. The definitiveness of LLM language was a recurring concern: experts recommended cautious phrasing, clear disclaimers positioning chatbots as informational tools rather than legal advisors, and avoiding action-oriented guidance. Participants also emphasised the need for clarifying questions before offering direction, as jurisdiction, procedural realities, and court timelines significantly shape outcomes. Together, these inputs underscore the importance of contextual grounding and restraint in legal AI responses.",
  },
];

const Notes: React.FC = () => {
  return (
    <Box
      w="100%"
      pt="80px"
      pb={{ base: 8, md: 12 }}
      px={{ base: 4, md: 6 }}
    >
      <VStack
        gap={{ base: '32px', sm: '40px', md: '48px', lg: '56px', xl: '60px' }}
        alignItems="flex-start"
        w="100%"
        maxW={{ base: '100%', md: '1200px' }}
        mx="auto"
      >
        {/* Section Heading */}
        <Text
          fontFamily="heading"
          fontSize={{ base: '28px', sm: '32px', md: '36px', lg: '40px' }}
          fontWeight="300"
          lineHeight="normal"
          letterSpacing="-1px"
          color="text.teal"
          w="100%"
        >
          Notes from specialist evaluations
        </Text>

        {/* Note Cards */}
        <VStack
          gap={{ base: '16px', sm: '18px', md: '20px' }}
          alignItems="center"
          w="100%"
        >
          {notesData.map((note, index) => (
            <NoteCard
              key={note.number}
              number={note.number}
              domain={note.domain}
              content={note.content}
              bgColor={index % 2 === 0 ? 'bg.mintWhite' : 'bg.limeWhite'}
            />
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};

export default Notes;
